import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { AppchargeCheckout } from "appcharge-checkout-reactjs-sdk";
import { Box, Button, Drawer, Stack, Typography } from "@mui/material";
import JSONInput from "react-json-editor-ajrm";
import { getInfraDev, getRuntimeEnv } from "../../runtimeConfig";

// This component owns the entire subscriptions test flow end-to-end
// (plans, session creation, checkout iframe) so the offer flow in
// Home.jsx never has to be touched to test subscriptions.
const env = getRuntimeEnv("REACT_APP_ENV", "dev");
const publisherToken =
  getRuntimeEnv("REACT_APP_PUBLISHER_TOKEN") ||
  "5fe9908d778bee1cc6aa3f22b05442ab367600fb118ea8d8509dd4365347f2d0";
const infraEnv = getInfraDev();

const LAMBDA_URL_MAP = {
  staging: "https://n8iiv57d99.execute-api.us-east-1.amazonaws.com/ac-stg",
  sandbox: "https://a3no38e5c1.execute-api.us-east-1.amazonaws.com/sandbox",
  production: "https://myf6de8jsh.execute-api.us-east-1.amazonaws.com/prod",
  dev: "https://rryhv338ih.execute-api.us-east-1.amazonaws.com/dev",
};
const lambdaUrl = infraEnv
  ? `https://checkout-standalone-backend-${infraEnv}.dev.appcharge-int.com`
  : LAMBDA_URL_MAP[env] || LAMBDA_URL_MAP.dev;

// OD has no dedicated lambda, so the standalone store calls checkout-gateway
// directly there (its CORS whitelist allows the OD origin). Every other
// environment proxies through the publisher-checkout-mock lambda.
const SUBSCRIPTIONS_CREATE_SESSION_ENDPOINT = infraEnv
  ? `https://checkout-gateway-${infraEnv}.dev.appcharge-int.com/checkout/subscriptions/session`
  : `${lambdaUrl}/createSubscriptionsSession`;

const GET_SUBSCRIPTION_PLANS_ENDPOINT = infraEnv
  ? `https://checkout-gateway-${infraEnv}.dev.appcharge-int.com/checkout/subscriptions/plans`
  : `${lambdaUrl}/subscriptionPlans`;

const CUSTOMER_SUBSCRIPTIONS_MANAGER_SESSION_ENDPOINT = infraEnv
  ? `https://checkout-gateway-${infraEnv}.dev.appcharge-int.com/checkout/subscriptions/customer-subscription-portal/session`
  : `${lambdaUrl}/customerSubscriptionPortalSession`;

const DEFAULT_CUSTOMER = {
  id: "John Doe",
  email: "test@appcharge.com",
};

const buildSessionPayload = (plan, customer) => ({
  customer,
  planId: plan._id,
  priceDetails: {
    price: plan.basePriceInUsd,
    currency: plan.supportedCurrencies?.[0]?.currencyCode || "usd",
  },
  sessionMetadata: {},
});

const textInputStyle = {
  width: "100%",
  padding: "8px 12px",
  borderRadius: "6px",
  border: "1px solid #444",
  background: "#2a2a2a",
  color: "white",
  fontSize: "14px",
  boxSizing: "border-box",
  outline: "none",
};

const cardStyle = {
  backgroundColor: "#1e1e1e",
  borderRadius: "12px",
  boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
  padding: "24px",
};

const LabeledInput = ({ id, label, ...inputProps }) => (
  <Stack gap={0.5}>
    <Typography
      component="label"
      htmlFor={id}
      fontSize="12px"
      color="rgba(255,255,255,0.6)"
    >
      {label}
    </Typography>
    <input id={id} style={textInputStyle} {...inputProps} />
  </Stack>
);

const PortalIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const LoadIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 12a9 9 0 1 1-2.64-6.36" />
    <polyline points="21 3 21 9 15 9" />
  </svg>
);

const Subscriptions = () => {
  const [hasLoaded, setHasLoaded] = useState(false);
  const [plans, setPlans] = useState([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [plansError, setPlansError] = useState("");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [sessionJSON, setSessionJSON] = useState(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [session, setSession] = useState();
  const [isIframeOpen, setIsIframeOpen] = useState(false);
  const [isIframeLoading, setIsIframeLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isManagerLoading, setIsManagerLoading] = useState(false);
  const [managerError, setManagerError] = useState("");
  const [customer, setCustomer] = useState(DEFAULT_CUSTOMER);

  const filteredPlans = plans.filter((plan) =>
    (plan.displayName || plan.name || "")
      .toLowerCase()
      .includes(searchQuery.toLowerCase()),
  );

  const loadPlans = () => {
    setHasLoaded(true);
    setIsLoadingPlans(true);
    setPlansError("");
    axios
      .get(GET_SUBSCRIPTION_PLANS_ENDPOINT, {
        headers: { "x-publisher-token": publisherToken },
        params: { offset: 0, pageSize: 100, status: "active" },
      })
      .then((res) => setPlans(res.data?.plans || []))
      .catch((err) => {
        console.log(err);
        setPlansError("Failed to load subscription plans");
      })
      .finally(() => setIsLoadingPlans(false));
  };

  const openCustomerPortal = () => {
    setIsManagerLoading(true);
    setManagerError("");
    axios
      .post(
        CUSTOMER_SUBSCRIPTIONS_MANAGER_SESSION_ENDPOINT,
        { customer },
        { headers: { "x-publisher-token": publisherToken } },
      )
      .then((res) => {
        if (res?.data?.subscriptionPortalUrl) {
          window.open(res.data.subscriptionPortalUrl, "_blank");
        }
      })
      .catch((err) => {
        console.log(err);
        setManagerError("Failed to open customer portal");
      })
      .finally(() => setIsManagerLoading(false));
  };

  const selectPlan = (plan) => {
    setSelectedPlan(plan);
    setSessionJSON(buildSessionPayload(plan, customer));
    setIsEditorOpen(true);
  };

  const createSubscriptionSession = () => {
    setIsIframeLoading(true);
    axios
      .post(SUBSCRIPTIONS_CREATE_SESSION_ENDPOINT, sessionJSON, {
        headers: { "x-publisher-token": publisherToken },
      })
      .catch((err) => console.log(err))
      .then((res) => {
        if (res?.data) {
          setIsEditorOpen(false);
          setIsIframeOpen(true);
          setSession({
            url: res.data.parsedUrl,
          });
        }
      });
  };

  return (
    <>
      <section
        className="subscriptions"
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          marginBottom: "4em",
        }}
      >
        <Stack
          direction="row"
          gap={2}
          flexWrap="wrap"
          justifyContent="center"
          sx={{ maxWidth: "90vw" }}
        >
          <Box sx={{ ...cardStyle, width: "280px" }}>
            <Typography color="white" variant="h6" fontWeight="bold" mb={2}>
              Customer
            </Typography>
            <Stack gap={2} mb={2}>
              <LabeledInput
                id="customer-id-input"
                label="Customer ID"
                type="text"
                value={customer.id}
                onChange={(e) =>
                  setCustomer({ ...customer, id: e.target.value })
                }
              />
              <LabeledInput
                id="customer-email-input"
                label="Customer Email"
                type="email"
                value={customer.email}
                onChange={(e) =>
                  setCustomer({ ...customer, email: e.target.value })
                }
              />
            </Stack>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<PortalIcon />}
              disabled={isManagerLoading}
              data-testid="open-customer-portal-button"
              onClick={openCustomerPortal}
              sx={{
                color: "#4caf50",
                borderColor: "#4caf50",
                borderWidth: "2px",
                "&:hover": {
                  borderColor: "#4caf50",
                  borderWidth: "2px",
                  backgroundColor: "rgba(76,175,80,0.1)",
                },
              }}
            >
              {isManagerLoading ? "Opening..." : "Chargebee Customer Portal"}
            </Button>
            {managerError && (
              <Typography color="error" fontSize="0.85rem" mt={1}>
                {managerError}
              </Typography>
            )}
          </Box>

          <Box sx={{ ...cardStyle, width: "380px" }}>
            <Typography color="white" variant="h6" fontWeight="bold" mb={2}>
              Plans{hasLoaded && !isLoadingPlans ? ` (${plans.length})` : ""}
            </Typography>

            {!hasLoaded && !isLoadingPlans && (
              <Box
                onClick={loadPlans}
                data-testid="load-subscriptions-button"
                sx={{
                  border: "1px dashed #555",
                  borderRadius: "8px",
                  padding: "32px 16px",
                  textAlign: "center",
                  cursor: "pointer",
                  color: "rgba(255,255,255,0.6)",
                  "&:hover": {
                    borderColor: "#888",
                    backgroundColor: "rgba(255,255,255,0.04)",
                  },
                }}
              >
                <LoadIcon />
                <Typography mt={1} fontSize="0.9rem">
                  Click to load plans
                </Typography>
              </Box>
            )}

            {isLoadingPlans && (
              <Stack alignItems="center" py={2}>
                <span className="loader"></span>
              </Stack>
            )}

            {plansError && (
              <Typography color="error" fontSize="0.85rem">
                {plansError}
              </Typography>
            )}

            {hasLoaded &&
              !isLoadingPlans &&
              !plansError &&
              (plans.length === 0 ? (
                <Typography color="white">No active plans found.</Typography>
              ) : (
                <>
                  <input
                    type="text"
                    placeholder={`Search ${plans.length} plans...`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{ ...textInputStyle, marginBottom: "12px" }}
                  />
                  <Stack
                    sx={{
                      maxHeight: "360px",
                      overflowY: "auto",
                      border: "1px solid #333",
                      borderRadius: "8px",
                    }}
                  >
                    {filteredPlans.map((plan) => (
                      <Box
                        key={plan._id}
                        data-testid={`plan-${plan._id}`}
                        onClick={() => selectPlan(plan)}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "10px 14px",
                          cursor: "pointer",
                          backgroundColor:
                            selectedPlan?._id === plan._id
                              ? "rgba(76,175,80,0.15)"
                              : "transparent",
                          borderBottom: "1px solid #333",
                          "&:hover": {
                            backgroundColor: "rgba(255,255,255,0.06)",
                          },
                          "&:last-of-type": { borderBottom: "none" },
                        }}
                      >
                        <Typography color="white" fontSize="0.9rem" noWrap>
                          {plan.displayName || plan.name}
                        </Typography>
                        <Typography
                          color="rgba(255,255,255,0.7)"
                          fontSize="0.8rem"
                          sx={{ whiteSpace: "nowrap", ml: 2 }}
                        >
                          {((plan.basePriceInUsd || 0) / 100).toFixed(2)} USD /{" "}
                          {plan.billingPeriodUnit}
                        </Typography>
                      </Box>
                    ))}
                    {filteredPlans.length === 0 && (
                      <Typography color="white" p={2} fontSize="0.85rem">
                        No plans match "{searchQuery}".
                      </Typography>
                    )}
                  </Stack>
                </>
              ))}
          </Box>
        </Stack>
      </section>

      <Drawer
        anchor="right"
        open={isEditorOpen}
        onClose={() => setIsEditorOpen(false)}
      >
        <Stack
          width={"600px"}
          height={"100%"}
          className="json-drawer"
          style={{ backgroundColor: "#1e1e1e" }}
          p={2}
          gap={2}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <h2 style={{ color: "white" }}>createSubscriptionSession</h2>
            <Button
              onClick={() => setIsEditorOpen(false)}
              sx={{ color: "white" }}
              variant="contained"
            >
              CLOSE
            </Button>
          </Stack>
          <Typography color="white">
            planId is auto-filled from the selected plan; override
            price/currency below if needed.
          </Typography>
          {sessionJSON && (
            <JSONInput
              key={selectedPlan?._id}
              onBlur={(e) => setSessionJSON(e.jsObject)}
              id="subscription_session_json"
              placeholder={sessionJSON}
              theme="dark"
              width="100%"
              height="300px"
              waitAfterKeyPress={300000}
              style={{ body: { fontSize: "14px" } }}
            />
          )}
          <Button
            variant="contained"
            data-testid="create-subscription-session-button"
            onClick={createSubscriptionSession}
          >
            Create Subscription Session
          </Button>
        </Stack>
      </Drawer>

      <div
        className="loading-iframe"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          backgroundColor: "rgba(0,0,0,0.75)",
          zIndex: 999999999,
          display: isIframeLoading ? "flex" : "none",
          justifyContent: "center",
          alignItems: "center",
          color: "white",
          fontSize: "2rem",
        }}
      >
        <span className="loader"></span>
      </div>
      {isIframeOpen && (
        <motion.div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100vh",
          }}
          initial={{ zIndex: 9999999 }}
        >
          <AppchargeCheckout
            onInitialLoad={() => setIsIframeLoading(false)}
            onOrderCreated={(params) => {
              console.log("onOrderCreated", { params });
              setIsIframeLoading(false);
            }}
            onClose={(params) => {
              console.log("onClose", { params });
              setIsIframeOpen(false);
            }}
            onOrderCompletedFailed={(params) =>
              console.log("onOrderCompletedFailed", { params })
            }
            onOrderCompletedSuccessfully={(params) =>
              console.log("onOrderCompletedSuccessfully", { params })
            }
            onPaymentIntentFailed={(params) =>
              console.log("onPaymentIntentFailed", { params })
            }
            onPaymentIntentSuccess={(params) =>
              console.log("onPaymentIntentSuccess", { params })
            }
            onPaymentIntent={(params) =>
              console.log("onPaymentIntent", { params })
            }
            checkoutUrl={session?.url}
          />
        </motion.div>
      )}
    </>
  );
};

export default Subscriptions;
